import { useState, type FC } from 'react';
import { Check, Copy } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, str } from '@/app/lib/a2ui/props.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { Button } from '@/app/lib/shadcdn/button.tsx';

const EMAIL = 'jason@orcasolutions.dev';

const messages = defineMessages({
  copyBrief: {
    id: 'serviceAdvisor.copyBrief',
    defaultMessage: 'Copy brief',
    description: 'Button that copies the complete service-advisor brief as plain text.',
  },
  copied: {
    id: 'serviceAdvisor.copied',
    defaultMessage: 'Copied',
    description: 'Short confirmation after the conversation brief is copied.',
  },
  copyFailed: {
    id: 'serviceAdvisor.copyFailed',
    defaultMessage: 'Copy did not work. Select and copy the brief manually.',
    description: 'Clipboard failure fallback for the selectable conversation brief.',
  },
  nextStep: {
    id: 'serviceAdvisor.nextStep',
    defaultMessage:
      'Next step: copy the brief and email it to {email} for a free first conversation (about 30 minutes, nothing to prepare). Nothing is sent or booked here.',
    description: 'Manual next step shown below the brief; nothing is sent or booked here.',
  },
});

const SECTION_ORDER = ['problem', 'today-and-better', 'fix-category', 'questions'] as const;

type BriefSection = {
  id: string;
  tag: string;
  heading: string;
  text: string;
};

function readSections(value: unknown): BriefSection[] {
  return arr(value)
    .filter(isRecord)
    .map((section) => ({
      id: str(section.id),
      tag: str(section.tag),
      heading: str(section.heading),
      text: str(section.text),
    }));
}

function orderedSections(sections: BriefSection[]): BriefSection[] {
  return SECTION_ORDER.flatMap((id) => {
    const section = sections.find((item) => item.id === id);
    return section ? [section] : [];
  });
}

function withoutWrappingQuotes(value: string): string {
  return value.trim().replace(/^["“”‘’']+|["“”‘’']+$/gu, '').trim();
}

function makePlainText(title: string, sections: BriefSection[], footer: string): string {
  return [
    title,
    ...sections.flatMap((section) => [`${section.tag} — ${section.heading}`, section.text]),
    withoutWrappingQuotes(footer),
  ]
    .filter((part) => part.length > 0)
    .join('\n\n');
}

export const ServiceAdvisorBrief: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const [copyResult, setCopyResult] = useState<{
    source: string;
    status: 'copied' | 'failed';
  } | null>(null);

  const title = str(node.props.title);
  const sections = orderedSections(readSections(node.props.sections));
  const footer = withoutWrappingQuotes(str(node.props.footer));
  const plainText = makePlainText(title, sections, footer);
  const complete =
    title.length > 0 &&
    footer.length > 0 &&
    sections.length === SECTION_ORDER.length &&
    sections.every((section) => section.tag && section.heading && section.text);
  const status = copyResult?.source === plainText ? copyResult.status : null;

  const copyBrief = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard-unavailable');
      await navigator.clipboard.writeText(plainText);
      setCopyResult({ source: plainText, status: 'copied' });
    } catch {
      setCopyResult({ source: plainText, status: 'failed' });
    }
  };

  return (
    <article className="jfy-brief-panel w-full min-w-0 max-w-prose break-words text-foreground">
      {title ? (
        <h1 className="mb-5 text-3xl font-semibold leading-tight tracking-tight text-balance">
          {title}
        </h1>
      ) : null}
      <div className="divide-y divide-border">
        {sections.map((section) => (
          <section key={section.id} className="py-4 first:pt-0">
            {section.tag ? (
              <p className="text-base font-semibold text-primary">{section.tag}</p>
            ) : null}
            {section.heading ? (
              <h2 className="mt-1 text-lg font-semibold leading-snug text-balance">
                {section.heading}
              </h2>
            ) : null}
            {section.text ? (
              <p className="mt-2 whitespace-pre-wrap text-base leading-relaxed">{section.text}</p>
            ) : null}
          </section>
        ))}
      </div>
      {footer ? (
        <p className="mt-5 whitespace-pre-wrap text-base leading-relaxed text-muted-foreground">
          {footer}
        </p>
      ) : null}
      {complete ? (
        <>
          <div className="mt-4 flex flex-col items-start gap-2">
            <Button
              type="button"
              variant="default"
              className="jfy-primary-action min-h-11 gap-2 px-4"
              onClick={copyBrief}
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {intl.formatMessage(messages.copyBrief)}
            </Button>
            {status ? (
              <p role="status" aria-live="polite" className="text-base text-muted-foreground">
                {status === 'copied' ? (
                  <span className="inline-flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                    {intl.formatMessage(messages.copied)}
                  </span>
                ) : (
                  intl.formatMessage(messages.copyFailed)
                )}
              </p>
            ) : null}
          </div>
          <p className="mt-5 text-base leading-relaxed">
            {intl.formatMessage(messages.nextStep, {
              email: (
                <a
                  className="text-primary underline underline-offset-2 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  href={`mailto:${EMAIL}`}
                >
                  {EMAIL}
                </a>
              ),
            })}
          </p>
        </>
      ) : null}
    </article>
  );
};
